/**
 * biometricService.js — Native Face ID / Touch ID integration
 *
 * Uses capacitor-native-biometric plugin to:
 *   1. Check if biometrics are available on the device
 *   2. Store credentials securely in the iOS Keychain
 *   3. Retrieve credentials after biometric verification
 *
 * Gracefully falls back to no-op if plugin is not installed,
 * running in browser, or device doesn't support biometrics.
 *
 * IMPORTANT: Uses STATIC import. Dynamic import stalls in WKWebView
 * during early initialization (same bug as contacts + biometric fixes).
 */

import { NativeBiometric } from '@capgo/capacitor-native-biometric';

const CREDENTIALS_KEY = 'clarime-auth';
const BIOMETRIC_STATE_KEY = 'clarime_biometric_enrolled';

/**
 * Native plugin error codes (mirrors BiometricAuthError in the plugin).
 * Swift throws these via call.reject() with code as string.
 */
const PLUGIN_ERROR = {
  UNKNOWN: 0,
  BIOMETRICS_UNAVAILABLE: 1,
  USER_LOCKOUT: 2,
  BIOMETRICS_NOT_ENROLLED: 3,
  AUTHENTICATION_FAILED: 10,
  APP_CANCEL: 11,
  INVALID_CONTEXT: 12,
  NOT_INTERACTIVE: 13,
  PASSCODE_NOT_SET: 14,
  SYSTEM_CANCEL: 15,
  USER_CANCEL: 16,
  USER_FALLBACK: 17,
};

/** Human-readable label for a plugin error code. */
function labelForErrorCode(code) {
  switch (Number(code)) {
    case PLUGIN_ERROR.BIOMETRICS_UNAVAILABLE: return 'biometrics unavailable';
    case PLUGIN_ERROR.USER_LOCKOUT: return 'biometric locked — unlock device with passcode first';
    case PLUGIN_ERROR.BIOMETRICS_NOT_ENROLLED: return 'no Face ID enrolled on device';
    case PLUGIN_ERROR.AUTHENTICATION_FAILED: return 'face not recognized';
    case PLUGIN_ERROR.APP_CANCEL: return 'app canceled — try again';
    case PLUGIN_ERROR.INVALID_CONTEXT: return 'internal state error';
    case PLUGIN_ERROR.NOT_INTERACTIVE: return 'app not in foreground';
    case PLUGIN_ERROR.PASSCODE_NOT_SET: return 'device passcode not set';
    case PLUGIN_ERROR.SYSTEM_CANCEL: return 'system canceled';
    case PLUGIN_ERROR.USER_CANCEL: return 'canceled';
    case PLUGIN_ERROR.USER_FALLBACK: return 'used fallback';
    default: return '';
  }
}

/**
 * True on iOS/Android Capacitor native runtime.
 */
function isNative() {
  return !!window.Capacitor?.isNativePlatform?.();
}

function getPlugin() {
  if (!isNative()) return null;
  return NativeBiometric ?? null;
}

/**
 * Check if biometric authentication is available on this device.
 * Returns: { isAvailable, biometryType, errorCode?, deviceIsSecure? }
 */
export async function checkBiometricAvailability() {
  const plugin = getPlugin();
  if (!plugin) {
    return { isAvailable: false, biometryType: 'none' };
  }

  try {
    const result = await plugin.isAvailable();
    // biometryType enum: 0=none, 1=touchId, 2=faceId, 3=fingerprint,
    //                    4=faceAuthentication, 5=iris, 6=multiple, 7=deviceCredential
    const typeMap = {
      1: 'touchId',
      2: 'faceId',
      3: 'faceId', // fingerprint → treat as "Face ID" label on Android (we show "Biometrics")
      4: 'faceId',
      5: 'faceId',
      6: 'faceId',
      7: 'faceId',
    };
    return {
      isAvailable: result.isAvailable,
      biometryType: typeMap[result.biometryType] || 'none',
      errorCode: result.errorCode,
      deviceIsSecure: result.deviceIsSecure,
    };
  } catch (err) {
    console.log('[Biometric] Availability check failed:', err?.message);
    return { isAvailable: false, biometryType: 'none' };
  }
}

/**
 * Enroll the user for biometric sign-in.
 *
 * Returns a structured result so the UI can surface a specific error:
 *   { ok: true }
 *   { ok: false, stage, code, message, userMessage }
 *
 *   stages:
 *     'no-plugin'       — not on native runtime
 *     'no-token'        — JWT was empty/missing (refused before prompting)
 *     'no-email'        — email was empty (required for Keychain `username`)
 *     'not-available'   — plugin says biometrics not available on device
 *     'verify'          — verifyIdentity threw (code/message from native)
 *     'set-credentials' — Keychain write failed
 *
 * @param {string} email
 * @param {string} firstName
 * @param {string} [token] - JWT to persist. Pass explicitly to avoid localStorage timing bugs.
 */
export async function enrollBiometric(email, firstName, token) {
  const plugin = getPlugin();
  if (!plugin) {
    return { ok: false, stage: 'no-plugin', userMessage: 'Biometrics not available.' };
  }

  // Keychain `username` can't be null/empty — Swift rejects "Missing properties".
  if (!email) {
    console.warn('[Biometric] Refusing to enroll without an email');
    return { ok: false, stage: 'no-email', userMessage: 'Missing email — please restart sign-in.' };
  }

  const authToken = token || localStorage.getItem('auth_token') || '';
  if (!authToken) {
    console.warn('[Biometric] Refusing to enroll without a JWT');
    return { ok: false, stage: 'no-token', userMessage: 'Please sign in again, then enable Face ID.' };
  }

  // Pre-flight availability check. `verifyIdentity` will throw with a
  // generic "Authentication not available" if the hardware/enrollment
  // state is wrong — checking first gives us a clean error upfront.
  const avail = await checkBiometricAvailability();
  if (!avail.isAvailable) {
    const label = avail.errorCode === 3 ? 'no Face ID is set up on this device' : 'biometrics are not available';
    console.warn('[Biometric] Not available:', avail);
    return {
      ok: false,
      stage: 'not-available',
      code: avail.errorCode,
      userMessage: `Couldn't enable Face ID — ${label}.`,
    };
  }

  // 1. Prompt for Face ID / Touch ID verification
  try {
    await plugin.verifyIdentity({
      reason: 'Enable Face ID for ClariMe',
      title: 'ClariMe',
      subtitle: 'Set up Face ID',
      description: 'Verify your identity to enable quick sign-in',
    });
  } catch (err) {
    const code = Number(err?.code ?? err?.errorCode ?? 0);
    const message = err?.message || '';
    console.log('[Biometric] verifyIdentity failed:', code, message);

    // Build a useful user-facing message
    const tail = labelForErrorCode(code) || message;
    const userMessage = code === PLUGIN_ERROR.USER_CANCEL
      ? '' // user-initiated cancel — don't show an error
      : `Couldn't enable Face ID${tail ? ` (${tail})` : ''}. Try again or tap Not now.`;

    return { ok: false, stage: 'verify', code, message, userMessage };
  }

  // 2. Persist credentials to Keychain
  //
  // The plugin's setCredentials does: SecItemAdd, on errSecDuplicateItem fall
  // back to SecItemUpdate. But the Add query matches by (account + server)
  // while the Update query matches by (server) only — a stale item from a
  // previous build with a different Keychain access group can hit both paths
  // inconsistently: Add says "duplicate" but Update says "item not found"
  // (KeychainError.noPassword — shown to the user as `.error 0`).
  //
  // Session 60 added App.entitlements (for Apple Sign In), which nudged the
  // app's default Keychain access group. Any Keychain item written by a
  // pre-Session-60 build ends up in this broken scope and poisons enrollment
  // forever — the Add→Update retry loop can never succeed.
  //
  // The fix: delete before write. Always a clean insert, no retry dance.
  // deleteCredentials no-ops if nothing exists, so this is safe on first run.
  try {
    await plugin.deleteCredentials({ server: CREDENTIALS_KEY }).catch((err) => {
      // Ignore delete failures — the next setCredentials will still work if
      // the item truly doesn't exist. We only care about failures that would
      // also block the write.
      console.log('[Biometric] delete-before-write noop:', err?.message);
    });

    await plugin.setCredentials({
      username: email,
      password: JSON.stringify({ firstName: firstName || '', token: authToken }),
      server: CREDENTIALS_KEY,
    });
  } catch (err) {
    const message = err?.message || 'Keychain write failed';
    console.log('[Biometric] setCredentials failed:', message);
    return {
      ok: false,
      stage: 'set-credentials',
      message,
      userMessage: `Couldn't save Face ID (${message}). Try again.`,
    };
  }

  // 3. Record enrollment state
  localStorage.setItem(
    BIOMETRIC_STATE_KEY,
    JSON.stringify({ enrolled: true, email, enrolledAt: new Date().toISOString() })
  );
  console.log('[Biometric] Enrollment succeeded for', email);
  return { ok: true };
}

/**
 * Attempt biometric sign-in.
 * Triggers Face ID / Touch ID, then retrieves stored credentials + JWT.
 * Returns: { success: true, email, firstName, token } or { success: false, error }
 */
export async function authenticateWithBiometric() {
  const plugin = getPlugin();
  if (!plugin) return { success: false, error: 'not_available' };

  try {
    await plugin.verifyIdentity({
      reason: 'Sign in to ClariMe',
      title: 'ClariMe',
      subtitle: 'Verify your identity',
      description: 'Use Face ID to access your account',
    });

    const credentials = await plugin.getCredentials({ server: CREDENTIALS_KEY });

    let firstName = '';
    let token = '';
    try {
      const data = JSON.parse(credentials.password);
      firstName = data.firstName || '';
      token = data.token || '';
    } catch {
      // Legacy format: password was just firstName or '_'
      firstName = credentials.password === '_' ? '' : credentials.password;
    }

    return {
      success: true,
      email: credentials.username,
      phone: credentials.username, // backward compat
      firstName,
      token,
    };
  } catch (err) {
    const code = Number(err?.code ?? err?.errorCode ?? 0);
    console.log('[Biometric] Auth failed:', code, err?.message);
    return {
      success: false,
      error: code === PLUGIN_ERROR.USER_CANCEL ? 'cancelled' : 'failed',
      code,
    };
  }
}

/**
 * Silently replace the JWT stored for biometric sign-in.
 *
 * Unlike enrollBiometric, this does NOT prompt Face ID — setCredentials is
 * a plain Keychain write; only getCredentials is gated behind verifyIdentity.
 * Call this after every successful sign-in and after every token refresh so
 * the Keychain copy can never go stale. (Stale-Keychain was half of the
 * build-32 expired-session trap: re-authenticating by email fixed the live
 * session but Face ID kept replaying the old dead token on the next launch.)
 *
 * No-ops unless biometric is already enrolled.
 */
export async function updateBiometricToken(email, firstName, token) {
  const plugin = getPlugin();
  if (!plugin || !isBiometricEnrolled() || !email || !token) return;

  try {
    // Delete-before-write: same Keychain access-group workaround as
    // enrollBiometric (see the long comment there).
    await plugin.deleteCredentials({ server: CREDENTIALS_KEY }).catch(() => {});
    await plugin.setCredentials({
      username: email,
      password: JSON.stringify({ firstName: firstName || '', token }),
      server: CREDENTIALS_KEY,
    });
    console.log('[Biometric] Stored token refreshed for', email);
  } catch (err) {
    // Non-fatal: the localStorage token still works for this session, and
    // the expiry check at next biometric login catches a stale Keychain.
    console.log('[Biometric] Token refresh write failed:', err?.message);
  }
}

export async function removeBiometricCredentials() {
  const plugin = getPlugin();
  if (!plugin) return;

  try {
    await plugin.deleteCredentials({ server: CREDENTIALS_KEY });
  } catch {
    // Ignore — credentials might not exist
  }
  localStorage.removeItem(BIOMETRIC_STATE_KEY);
}

export function isBiometricEnrolled() {
  try {
    const state = JSON.parse(localStorage.getItem(BIOMETRIC_STATE_KEY) || '{}');
    return !!state.enrolled;
  } catch {
    return false;
  }
}

export async function getBiometryLabel() {
  const { biometryType } = await checkBiometricAvailability();
  if (biometryType === 'faceId') return 'Face ID';
  if (biometryType === 'touchId') return 'Touch ID';
  return 'Biometrics';
}
